import { PRODUCT_CONFIG } from "@stb/product-config"
import { createFileRoute } from "@tanstack/react-router"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import config from "@/config"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import {
  ATO_RECORDS_CHECKED,
  ATO_RECORDS_URL,
  formatCheckedOn,
} from "../components/home/home-content"
import { AnswerBox, Breadcrumb, CtaBand, SimpleTable, SourceLine } from "../components/landing"

/** Copy source: docs/design/landing/copy.md section 2.6. */
export const howItWorksSeo = () =>
  buildHead({
    title: `How ${config.core.projectName} works: trips, receipts, invoices`,
    description:
      "Install the app, drive and snap receipts. Review trips on the web, then export your trip log as CSV for your accountant at tax time.",
    path: "/how-it-works",
    jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "How it works" }])],
  })

export const Route = createFileRoute("/_brand-layout/how-it-works")({
  head: () => {
    const { meta, links, scripts } = howItWorksSeo()
    return { meta, links, scripts }
  },
  component: HowItWorksPage,
})

/** Only features that ship today (copy.md 2.6; T389 review). GST exports wait for their task. Web receipts and invoices ship; the Logbook plan has no receipts or invoicing. */
const STEPS = [
  {
    title: "Install and sign in",
    body: "Use your email. No password to remember. Add your car.",
  },
  {
    title: "Drive and snap",
    body: "Trips record in the background. Photograph receipts and create invoices as you go.",
  },
  {
    title: "Review and export",
    body: "Check and classify trips on the web. Download your trip log as CSV and send it to your accountant.",
  },
] as const

const PHONE_VS_WEB = [
  {
    header: "Trips",
    phone: "Records each drive with GPS in the background.",
    web: "Review trips, add a reason, and classify several at once.",
  },
  {
    header: "Receipts",
    phone:
      "Photograph a receipt. You check what we read before it counts. Not in the Logbook plan.",
    web: "Check what we read, fix it, and see receipts with your expenses.",
  },
  {
    header: "Invoices",
    phone: "Create invoices and see who has paid. Not in the Logbook plan.",
    web: "Create invoices, see who has paid, and manage customers.",
  },
  {
    header: "Reports",
    phone: "Not on the phone yet.",
    web: "Download your trip log as CSV, or export all your data as a zip.",
  },
] as const

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

export function HowItWorksPage() {
  const checked = formatCheckedOn(ATO_RECORDS_CHECKED)
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "How it works" }]} />
      <header className="bg-muted/40 px-band py-8 md:py-12">
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">How it works</h1>
        <p className="text-muted-foreground mt-3 max-w-[60ch] text-lg">
          Your phone records. The web is where you check things and get your reports.
        </p>
        <div className="mt-6 max-w-[70ch]">
          <AnswerBox
            source={
              <SourceLine
                sources={[{ label: "ATO, record keeping overview", href: ATO_RECORDS_URL }]}
                checked={checked}
              />
            }
          >
            Install the app, add your car, and drive. Trips record on your phone and you review them
            on the web. Receipts are photographed as you go. At tax time you export your trip log as
            CSV for your accountant. The ATO asks you to keep most business records for 5 years, so
            keep your own copy of each export.
          </AnswerBox>
        </div>
      </header>

      <section className="px-band grid gap-4 py-8" aria-labelledby="steps-title">
        <h2 id="steps-title" className="text-2xl font-semibold">
          Three steps
        </h2>
        <ol className="grid gap-4 md:grid-cols-3">
          {STEPS.map(({ title, body }, i) => (
            <li key={title} className="flex gap-4">
              <span
                aria-hidden="true"
                className="bg-primary text-primary-foreground grid size-9 shrink-0 place-items-center rounded-full font-bold"
              >
                {i + 1}
              </span>
              <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-muted-foreground mt-1">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="px-band grid gap-4 py-8" aria-labelledby="split-title">
        <h2 id="split-title" className="text-2xl font-semibold">
          Phone records, web checks and reports
        </h2>
        <SimpleTable
          caption="Phone and web: what each one does"
          columns={["Job", "On your phone", "On the web"]}
          rows={PHONE_VS_WEB.map((r) => ({ header: r.header, cells: [r.phone, r.web] }))}
        />
        <p className="max-w-[70ch]">
          Details for each job: the{" "}
          <a href="/mileage-tracker" className={linkClass}>
            mileage tracker
          </a>
          , the{" "}
          <a href="/receipt-scanner" className={linkClass}>
            receipt scanner
          </a>{" "}
          and{" "}
          <a href="/invoicing" className={linkClass}>
            invoicing
          </a>
          .
        </p>
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="data-title">
        <h2 id="data-title" className="text-2xl font-semibold">
          What happens to your data
        </h2>
        <p className="max-w-[70ch]">
          Trips are recorded on your phone and sync to your account when you are online. Receipt
          images and assistant messages go to Anthropic (outside Australia) only when you use AI
          receipt reading or the assistant. Read{" "}
          <a href="/trust" className={linkClass}>
            where your data lives
          </a>{" "}
          for the full list of services and how to export or delete your data. Other questions are
          answered in the{" "}
          <a href="/faq" className={linkClass}>
            FAQ
          </a>
          .
        </p>
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="free-title">
        <h2 id="free-title" className="text-2xl font-semibold">
          Start free
        </h2>
        <p className="max-w-[70ch]">
          The free plan records {PRODUCT_CONFIG.plans.free.tripsMonthly} GPS trips a month. New
          accounts also get a {TRIAL_DAYS}-day free trial of Logbook or Pro, no card needed.
          {PRODUCT_CONFIG.pricesDraft && " Plan prices are drafts until confirmed."}{" "}
          <a href="/pricing" className={linkClass}>
            See pricing
          </a>
        </p>
      </section>

      <section
        role="note"
        aria-label="What Kestly does not do"
        className="bg-accent text-accent-foreground mx-band my-4 grid gap-1 rounded-lg px-5 py-4"
      >
        <h2 className="font-semibold">Limits</h2>
        <p>
          Kestly does not lodge your BAS or your tax return, and it does not give tax advice.
          Deduction figures are estimates. Check them with your accountant or the ATO.
        </p>
      </section>

      <CtaBand
        title="Ready to try it?"
        description={`${TRIAL_DAYS}-day free trial. No card needed.`}
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
